import { useState, useEffect, useRef } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isTransparent = !scrolled && isHome;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center px-4 md:px-10 transition-all duration-300 ${
          isTransparent
            ? 'bg-foreground-900/15 backdrop-blur-md'
            : 'bg-background-50/95 backdrop-blur-xl border-b border-background-200/70'
        }`}
      >
        <div className="w-full max-w-[1280px] mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer ${
              isTransparent ? 'text-white' : 'text-foreground-900'
            }`}
          >
            CSSBuy<span className={`font-light italic ${isTransparent ? 'text-white/70' : 'text-foreground-600'}`}>.wiki</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => navigate('/')}
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Início
            </button>

            <button
              onClick={() => navigate('/guia-de-compras')}
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              <i className="ri-book-open-line mr-1.5"></i>
              Guia de Compras
            </button>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isTransparent
                    ? 'bg-white/15 text-white hover:bg-white/25'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <span>Categorias</span>
                {isOpen ? (
                  <i className="ri-arrow-up-s-line text-base"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-base"></i>
                )}
              </button>

              {isOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-background-50 rounded-2xl border border-background-200/70 py-2 max-h-[420px] overflow-y-auto"
                  style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}
                >
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.link}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <span className="w-0.5 h-4 rounded-full bg-accent-500 opacity-0 group-hover:opacity-100 transition-opacity mr-3 flex-shrink-0"></span>
                      <span className="truncate">{cat.namePt}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="relative px-5 py-2.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer group"
            >
              <span>Buy It Now</span>
              <span className="inline-block ml-1.5 group-hover:translate-x-0.5 transition-transform duration-300">→</span>
            </a>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-foreground-900 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                isTransparent ? 'text-white hover:bg-white/10' : 'text-foreground-700 hover:bg-background-100'
              }`}
              aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {mobileMenuOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay — rendered OUTSIDE nav to avoid stacking context issues */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[999]">
          {/* Dark overlay */}
          <div
            className="absolute inset-0 bg-foreground-900/40"
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          {/* Menu panel */}
          <div className="absolute top-[72px] left-0 right-0 bottom-0 bg-background-50 overflow-y-auto animate-slide-down">
            <div className="px-4 py-4 space-y-1">
              <button
                onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer text-sm font-medium"
              >
                <i className="ri-home-4-line text-lg"></i>
                <span>Início</span>
              </button>
              <button
                onClick={() => { navigate('/guia-de-compras'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer text-sm font-medium"
              >
                <i className="ri-book-open-line text-lg"></i>
                <span>Guia de Compras</span>
              </button>

              <div className="h-px bg-background-200 my-3 mx-2"></div>

              <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-[0.15em]">
                Categorias
              </p>

              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer text-sm group"
                >
                  <span className="w-1 h-5 rounded-full bg-accent-500 flex-shrink-0"></span>
                  <span className="flex-1">{cat.namePt}</span>
                  <i className="ri-arrow-right-up-line text-xs text-foreground-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </a>
              ))}

              <div className="pt-4 pb-2">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-xl hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <span>Buy It Now</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}